import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Rating,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { userApi, type RetailerRating } from '../../services/userApi';
import type { RetailerRatingSummary } from '../../services/retailersApi';
import { useAuthStore } from '../../stores/authStore';

interface RetailerRatingControlProps {
  /** The Retailer id being rated. */
  retailerId: string;
  /** Retailer name for accessible naming. */
  retailerName: string;
  /** The public aggregate-only rating summary from the retailer detail read. */
  summary: RetailerRatingSummary;
}

const COMMENT_MAX = 2000;

/**
 * Read-only aggregate display (issue #9, spec §12.3). Aggregate-only — it never
 * shows individual raters. Below the min-sample gate it says so explicitly so a
 * thin sample isn't presented as a settled score.
 */
function RatingAggregate({ summary }: { summary: RetailerRatingSummary }) {
  if (summary.count === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No customer ratings yet. Be the first to rate this retailer.
      </Typography>
    );
  }

  const shown = summary.established ? summary.trimmedAverage : summary.average;
  const value = typeof shown === 'number' ? shown : 0;
  const label = `Average customer rating ${value.toFixed(1)} out of 5, from ${summary.count} ${
    summary.count === 1 ? 'rating' : 'ratings'
  }`;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Rating
        value={value}
        precision={0.1}
        readOnly
        aria-label={label}
        emptyIcon={<StarBorderIcon fontSize="inherit" />}
        icon={<StarIcon fontSize="inherit" />}
      />
      <Typography variant="body2">
        {value.toFixed(1)} / 5 ({summary.count} {summary.count === 1 ? 'rating' : 'ratings'})
      </Typography>
      {!summary.established && (
        <Typography variant="caption" color="text.secondary">
          Not enough ratings yet to establish a score (need {summary.minSample}).
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Per-retailer rating control (issue #9 — rate retailers). Mirrors the
 * FavoriteRetailerButton / WatchButton auth pattern: logged-out users are
 * routed to /login with a return path; logged-in users submit a 1–5 star
 * rating with an optional comment via PUT /user/retailer-ratings (idempotent
 * upsert). The control prefills from the caller's existing rating so revisions
 * are in place. The public aggregate (separate from the curated trust score) is
 * shown above the control.
 */
export function RetailerRatingControl({
  retailerId,
  retailerName,
  summary,
}: RetailerRatingControlProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [stars, setStars] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null
  );

  const ratingsQuery = useQuery({
    queryKey: ['retailer-ratings'],
    queryFn: () => userApi.listRetailerRatings(),
    enabled: isAuthenticated,
    retry: false,
  });

  const existing: RetailerRating | undefined = ratingsQuery.data?.ratings.find(
    (r) => r.retailerId === retailerId
  );

  // Prefill once the caller's existing rating loads. "Adjust state during
  // render" (tracking the previously-synced rating) instead of an effect.
  const [syncedRating, setSyncedRating] = useState(existing);
  if (existing && existing !== syncedRating) {
    setSyncedRating(existing);
    setStars(existing.rating);
    setComment(existing.comment ?? '');
  }

  const submitMutation = useMutation({
    mutationFn: () =>
      userApi.upsertRetailerRating({
        retailerId,
        rating: stars as number,
        comment: comment.trim() === '' ? null : comment.trim(),
      }),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Thanks — your rating has been saved.' });
      void queryClient.invalidateQueries({ queryKey: ['retailer-ratings'] });
      void queryClient.invalidateQueries({ queryKey: ['retailer', retailerId] });
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t save your rating: ${err.message}` });
    },
  });

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!isAuthenticated) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/login?return=${encodeURIComponent(returnPath)}`);
      return;
    }
    if (stars == null || stars < 1) {
      setToast({ severity: 'error', message: 'Choose a star rating from 1 to 5 first.' });
      return;
    }
    submitMutation.mutate();
  }

  const hasExisting = Boolean(existing);

  return (
    <Box component="section" aria-labelledby="retailer-rating-heading">
      <Typography id="retailer-rating-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 1 }}>
        Customer ratings
      </Typography>

      <Box sx={{ mb: 2 }}>
        <RatingAggregate summary={summary} />
      </Box>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2} alignItems="flex-start">
          <FormControl component="fieldset">
            <FormLabel component="legend" id="your-rating-label">
              {hasExisting ? `Update your rating for ${retailerName}` : `Rate ${retailerName}`}
            </FormLabel>
            <RadioGroup
              row
              name="retailer-rating"
              aria-labelledby="your-rating-label"
              value={stars == null ? '' : String(stars)}
              onChange={(event) => setStars(Number(event.target.value))}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <FormControlLabel
                  key={value}
                  value={String(value)}
                  control={<Radio />}
                  label={value === 1 ? '1 star' : `${value} stars`}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Add a comment (optional)"
            value={comment}
            onChange={(event) => setComment(event.target.value.slice(0, COMMENT_MAX))}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: COMMENT_MAX, 'aria-describedby': 'comment-help' }}
            helperText="Comments are reviewed before they appear publicly."
            id="retailer-rating-comment"
          />
          <span id="comment-help" hidden>
            Your comment is reviewed before it appears publicly.
          </span>

          <Button type="submit" variant="contained" disabled={submitMutation.isPending}>
            {hasExisting ? 'Update rating' : 'Submit rating'}
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

export default RetailerRatingControl;
